import { MailOutlined, SafetyOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { Button, Space, Switch, Tag, Typography, notification } from 'antd';
import React, { useCallback, useState } from 'react';
import YesNoModal from '../../../components/YesNoModal';
import {
  sendPasswordResetToUserMutation,
  setUserSecurityMutation,
} from '../constants';

const { Title, Paragraph, Text } = Typography;

function UserSecurityTab({ user, refetch }) {
  const [savingTwoFactor, setSavingTwoFactor] = useState(false);
  const [confirmReset, setConfirmReset] = useState(null);
  const [confirmSendEmail, setConfirmSendEmail] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const [setUserSecurity] = useMutation(setUserSecurityMutation);
  const [sendPasswordResetToUser] = useMutation(
    sendPasswordResetToUserMutation,
  );

  const onChangeTwoFactor = useCallback(
    async (checked) => {
      setSavingTwoFactor(true);
      try {
        await setUserSecurity({
          variables: { userId: user._id, twoFactorEnabled: checked },
        });
        notification.success({
          message: 'Saved',
          description: checked
            ? 'Two-factor authentication turned on for this user'
            : 'Two-factor authentication turned off for this user',
        });
        if (refetch) {
          refetch();
        }
      } catch (err) {
        console.error(err);
        notification.error({
          message: 'Error',
          description: err.message,
        });
      }
      setSavingTwoFactor(false);
    },
    [user, setUserSecurity, refetch],
  );

  const onConfirmReset = useCallback(async () => {
    setWorking(true);
    setError(null);
    try {
      await setUserSecurity({
        variables: { userId: user._id, shouldResetPassword: confirmReset },
      });
      notification.success({
        message: 'Saved',
        description: confirmReset
          ? 'This user must reset their password at next sign-in'
          : 'Required password reset cancelled',
      });
      setConfirmReset(null);
      if (refetch) {
        refetch();
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
    setWorking(false);
  }, [user, setUserSecurity, confirmReset, refetch]);

  const onConfirmSendEmail = useCallback(async () => {
    setWorking(true);
    setError(null);
    try {
      await sendPasswordResetToUser({ variables: { userId: user._id } });
      notification.success({
        message: 'Sent',
        description: `A password reset link was emailed to ${user.email}`,
      });
      setConfirmSendEmail(false);
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
    setWorking(false);
  }, [user, sendPasswordResetToUser]);

  if (!user) {
    return null;
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Title level={4}>
          <SafetyOutlined /> Two-Factor Authentication
        </Title>
        <Paragraph type="secondary">
          When on, this user must enter a 6-digit code emailed to them each time
          they sign in with their password. This is a shared setting — the user
          can turn it back off from their own profile.
        </Paragraph>
        <Switch
          checked={!!user.twoFactorEnabled}
          loading={savingTwoFactor}
          onChange={onChangeTwoFactor}
          checkedChildren="On"
          unCheckedChildren="Off"
        />
      </div>

      <div>
        <Title level={4}>Password</Title>
        <Paragraph type="secondary">
          Require a reset to block sign-in until this user chooses a new
          password. We email them a reset link automatically when they try.
        </Paragraph>
        <Paragraph>
          Reset required:{' '}
          {user.shouldResetPassword ? (
            <Tag color="orange">Yes</Tag>
          ) : (
            <Tag>No</Tag>
          )}
        </Paragraph>
        <Space wrap>
          {/* Both modals share one `error`, so clear it on open -- otherwise a
              failure from one renders inside the other when it next appears. */}
          {!user.shouldResetPassword && (
            <Button
              onClick={() => {
                setError(null);
                setConfirmReset(true);
              }}
            >
              Require Password Reset
            </Button>
          )}
          {user.shouldResetPassword && (
            <Button
              onClick={() => {
                setError(null);
                setConfirmReset(false);
              }}
            >
              Cancel Required Reset
            </Button>
          )}
          <Button
            icon={<MailOutlined />}
            onClick={() => {
              setError(null);
              setConfirmSendEmail(true);
            }}
          >
            Send Password Reset Email
          </Button>
        </Space>
      </div>

      <YesNoModal
        title="Password Reset"
        question={
          confirmReset
            ? `Require ${user.name} to reset their password at next sign-in?`
            : `Cancel the required password reset for ${user.name}?`
        }
        yesText="Yes"
        noText="Cancel"
        onYes={onConfirmReset}
        onNo={() => {
          setConfirmReset(null);
          setError(null);
        }}
        visible={confirmReset !== null}
        loading={working}
        error={error}
      />
      <YesNoModal
        title="Send Password Reset Email"
        question={
          <Text>{`Email a password reset link to ${user.email}?`}</Text>
        }
        yesText="Send"
        noText="Cancel"
        onYes={onConfirmSendEmail}
        onNo={() => {
          setConfirmSendEmail(false);
          setError(null);
        }}
        visible={confirmSendEmail}
        loading={working}
        error={error}
      />
    </Space>
  );
}

export default UserSecurityTab;
